import { useState, useEffect, type RefObject } from 'react'

type Position = {
  x: number
  y: number
}

type UseDragAndDropOptions = {
  containerRef: RefObject<HTMLDivElement>
  elementRef: RefObject<HTMLDivElement>
  initialPosition?: Position
}

type UseDragAndDropReturn = {
  position: Position
  isDragging: boolean
  isHovered: boolean
  setIsHovered: (hovered: boolean) => void
  handleMouseDown: (e: React.MouseEvent) => void
  handleTouchStart: (e: React.TouchEvent) => void
}

export function useDragAndDrop({
  containerRef,
  elementRef,
  initialPosition = { x: 0, y: 0 }
}: UseDragAndDropOptions): UseDragAndDropReturn {
  const [position, setPosition] = useState<Position>(initialPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 })

  useEffect(() => {
    const updateInitialPosition = () => {
      if (containerRef.current && elementRef.current) {
        const container = containerRef.current.getBoundingClientRect()
        const element = elementRef.current.getBoundingClientRect()

        setPosition({
          x: container.width - element.width - 24,
          y: container.height - element.height - 24
        })
      }
    }

    updateInitialPosition()
    window.addEventListener('resize', updateInitialPosition)
    return () => window.removeEventListener('resize', updateInitialPosition)
  }, [containerRef, elementRef])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    })
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    setIsDragging(true)
    const touch = e.touches[0]
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    })
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current || !elementRef.current) return

    const container = containerRef.current.getBoundingClientRect()
    const element = elementRef.current.getBoundingClientRect()

    let newX = e.clientX - dragStart.x
    let newY = e.clientY - dragStart.y

    newX = Math.max(0, Math.min(newX, container.width - element.width))
    newY = Math.max(0, Math.min(newY, container.height - element.height))

    setPosition({ x: newX, y: newY })
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !containerRef.current || !elementRef.current) return

    const container = containerRef.current.getBoundingClientRect()
    const element = elementRef.current.getBoundingClientRect()
    const touch = e.touches[0]

    let newX = touch.clientX - dragStart.x
    let newY = touch.clientY - dragStart.y

    newX = Math.max(0, Math.min(newX, container.width - element.width))
    newY = Math.max(0, Math.min(newY, container.height - element.height))

    setPosition({ x: newX, y: newY })
  }

  const handleDragEnd = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleDragEnd)
      window.addEventListener('touchmove', handleTouchMove)
      window.addEventListener('touchend', handleDragEnd)

      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleDragEnd)
        window.removeEventListener('touchmove', handleTouchMove)
        window.removeEventListener('touchend', handleDragEnd)
      }
    }
  }, [isDragging, dragStart, position])

  return {
    position,
    isDragging,
    isHovered,
    setIsHovered,
    handleMouseDown,
    handleTouchStart
  }
}
